import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LIMIT } from '@authority-labs/constants';
import { ErrorEnum, Path } from '@authority-labs/enums';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  AccountReplacedError,
  AuthenticationService,
  RefreshSupersededError,
  SessionEndedError
} from './authentication.service';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthenticationService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Auth calls (login, refresh, logout) act for whoever signs in next.
    if (!this.isAuthRequest(req) && this.authService.isAccountReplaced()) {
      // A tab that missed the storage event (back-forward cache, frozen)
      // would otherwise fail every request until reloaded by hand.
      this.authService.followReplacedAccount();
      return throwError(new AccountReplacedError());
    }
    const sent = this.addToken(
      req,
      this.authService.getAuthToken(),
      this.authService.getUserId()
    );
    return next.handle(sent).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          const sentToken = sent.headers
            .get('Authorization')
            ?.replace(/^Bearer /, '');
          return this.handle401(req, next, error, sentToken ?? null);
        }
        return throwError(error);
      })
    );
  }

  private handle401(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse,
    sentToken: string | null
  ): Observable<HttpEvent<unknown>> {
    // Wrong-password case: don't try to refresh — the credentials are invalid,
    // not the session.
    if (error.error?.message === ErrorEnum.NotAccepted) {
      if (this.isPasswordAttemptsExceeded()) {
        this.authService.logout();
      }
      return throwError(error);
    }

    // A 401 from refresh itself is terminal - refreshing again would loop.
    // The refresh's caller below decides about logout: only it knows whether
    // storage still holds the login this refresh was for.
    if (this.isRefreshRequest(req)) {
      return throwError(error);
    }

    if (this.isLoginRequest(req)) {
      this.fullLogout();
      return throwError(error);
    }

    // No refresh token at all (a session predating this feature) — preserve
    // the original logout-on-401 behaviour.
    if (!this.authService.getRefreshToken()) {
      this.fullLogout();
      return throwError(error);
    }

    // N requests refused together share one refresh (see refresh()). Only a
    // failed refresh ends the session. The catchError sits before the replay
    // so an error from the replayed request itself (a 409 conflict, a 403)
    // reaches the caller instead of logging the user out.
    return this.authService.refresh(sentToken).pipe(
      catchError((refreshError) => {
        // The refresh classifies its own failure against the token it sent;
        // this request may have joined a refresh for an earlier login.
        if (refreshError instanceof SessionEndedError) {
          // Never for an outage (5xx, network): logout clears local drafts.
          this.fullLogout(true);
          return throwError(error);
        }
        if (
          refreshError instanceof RefreshSupersededError &&
          this.authService.getRefreshToken()
        ) {
          // Another login replaced the one this refresh was for (a password
          // change, a sign-in in another tab). Replay with that login if it
          // is the same account (refresh() refuses another account's), once.
          return this.authService
            .refresh(sentToken)
            .pipe(catchError(() => throwError(error)));
        }
        return throwError(
          refreshError instanceof RefreshSupersededError ? error : refreshError
        );
      }),
      switchMap((authResp) =>
        next.handle(this.replayWithToken(req, authResp.token))
      )
    );
  }

  // The original request already carries the now-stale Authorization header,
  // and addToken deliberately won't overwrite an existing one — so set it
  // explicitly here.
  private replayWithToken(
    req: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-User-Identifier': this.authService.getUserId()
      }
    });
  }

  private isAuthRequest(req: HttpRequest<unknown>): boolean {
    return req.url.includes(`/${Path.Auth}/`);
  }

  private isRefreshRequest(req: HttpRequest<unknown>): boolean {
    return req.url.endsWith(`/${Path.Auth}/${Path.Refresh}`);
  }

  /** Password login, or the SSO code exchange: a 401 there is no session's. */
  private isLoginRequest(req: HttpRequest<unknown>): boolean {
    return (
      req.url.endsWith(`/${Path.Auth}/${Path.Login}`) ||
      req.url.endsWith(`/${Path.Auth}/microsoft/exchange`)
    );
  }

  /** `sessionEnded`: the next login returns to the current page. */
  private fullLogout(sessionEnded = false): void {
    if (this.router.url.indexOf('login') === -1) {
      this.authService.logout(sessionEnded);
    }
  }

  addToken(
    req: HttpRequest<unknown>,
    token: string,
    userId: string
  ): HttpRequest<unknown> {
    if (token) {
      if (req.headers.has('Authorization')) {
        return req.clone({
          setHeaders: {
            'X-User-Identifier': userId
          }
        });
      } else {
        return req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            'X-User-Identifier': userId
          }
        });
      }
    } else {
      return req;
    }
  }

  private isPasswordAttemptsExceeded() {
    return ++this.authService.passwordAttemptsCount >= LIMIT.MAX_PASS_ATTEMPTS;
  }
}
